import React, { useEffect, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import Drawer from './Drawer';
import clsx from 'clsx';
import { translate } from '../Services/translation';
import LanguageDropdown from './LanguageDropdown';
import { Language } from './LanguageSelector';

interface MobileMenuProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  links: NavigationItem[];
  additionalHeaderLinks: AdditionalHeaderLinks[];
  languageList?: Language | null;
  block?: any;
}

interface AdditionalHeaderLinks {
  Name: string;
  Url: string;
}


interface NavigationItem {
  disabled?: boolean;
  iconUrl?: string;
  name: string;
  url: string;
  links?: NavigationItem[];
}

function getParentLinkName(
  links: NavigationItem[],
  activePath: number[]
): string | null {
  let parent: NavigationItem | null = null;
  let currentLevel = links;
  for (let i = 0; i < activePath.length; i++) {
    const idx = activePath[i];
    if (typeof idx === 'number' && currentLevel[idx]) {
      parent = currentLevel[idx];
      currentLevel = parent.links || [];
    } else {
      break;
    }
  }
  return parent ? parent.name : null;
}

const NavigationPanel: React.FC<{
  links: NavigationItem[];
  activePath: number[];
  setActivePath: (path: number[]) => void;
  additionalHeaderLinks?: AdditionalHeaderLinks[];
  languageList?: Language | null;
  setParentLinkName?: (name: string | null) => void;
  parentLinkName?: string | null;
}> = ({
  links,
  activePath,
  setActivePath,
  additionalHeaderLinks,
  languageList,
  setParentLinkName,
}) => {
  const handleLinkButtonClick = (index: number) => {
    if (setParentLinkName) {
      let currentLevel = links;
      for (let i = 0; i < activePath.length; i++) {
        const idx = activePath[i];
        if (typeof idx === 'number' && currentLevel[idx]) {
          currentLevel = currentLevel[idx].links || [];
        } else {
          currentLevel = [];
          break;
        }
      }
      setParentLinkName(currentLevel[index]?.name || null);
    }
    setActivePath([...activePath, index]);
  };

  const isFirstLevel = activePath.length === 0;
  const renderLinks = (items: NavigationItem[]) => (
    <ul className="h-full flex flex-col overflow-y-auto" role="menu">
      {items.map((item, index) => {
        const hasChildren = item.links && item.links.length > 0;

        return (
          <li
            className="flex w-full justify-between pl-[22px] border-b navigation-drawer__menu-item"
            key={index}
            role="menuitem"
          >
            <>
              <a
                href={item.url}
                className={clsx(
                  'flex items-center flex-1 pt-[15px] pb-[13px]',
                  isFirstLevel
                    ? 'font-bold text-large transition-colors'
                    : 'text-medium'
                )}
              >
                {isFirstLevel && (
                  <>
                    {item.iconUrl ? (
                      <img
                        src={item.iconUrl}
                        alt=""
                        className="inline-block mr-4 w-[40px] h-[40px] transition-opacity"
                      />
                    ) : (
                      <span className="inline-block mr-2 w-10 h-10 navigation-drawer__image-placeholder"></span>
                    )}
                  </>
                )}
                {item.name}
              </a>
              {hasChildren && (
                <button
                  onClick={() => handleLinkButtonClick(index)}
                  className="ml-2 text-white aspect-square h-full"
                  aria-label={`${translate('menu.submenu-for')} ${item.name}`}
                  aria-expanded={
                    activePath[activePath.length - 1] === index
                      ? 'true'
                      : 'false'
                  }
                >
                  <i className="fal fa-arrow-right text-white "></i>
                </button>
              )}
            </>
          </li>
        );
      })}
      {isFirstLevel &&
        additionalHeaderLinks?.map(
          (link: AdditionalHeaderLinks, index: number) => (
            <li
              key={index}
              className="flex w-full justify-between pr-5 pl-[22px] navigation-drawer__menu-item border-b"
              role="menuitem"
            >
              <a
                href={link.Url}
                className="text-white flex flex-1 pt-[15px] pb-[13px] text-base font-bold transition-colors"
              >
                {link.Name}
              </a>
            </li>
          )
        )}
      {isFirstLevel && languageList?.LanguageList?.length > 1 && (
        <li
          className="w-full pr-5 pt-[15px] pb-[13px] navigation-drawer__language-selector border-b"
          role="menuitem"
        >
          <LanguageDropdown languageList={languageList} inline />
        </li>
      )}
    </ul>
  );

  const currentLinks = activePath.reduce(
    (current, index) => current[index]?.links || [],
    links
  );

  const setFocusToFirstLink = () => {
    const firstLink = document.querySelector('.navigation-drawer__menu-item a');
    if (firstLink instanceof HTMLElement) {
      firstLink.focus();
    }
  };
  useEffect(() => {
    if (!isFirstLevel) {
      setFocusToFirstLink();
    }
  }, [activePath, isFirstLevel, links]);

  return (
    <TransitionGroup component={null}>
      <CSSTransition
        key={activePath.join('-') || 'root'}
        timeout={200} // Animation duration
        classNames="fade"
      >
        <div className="flex flex-col gap-2 flex-1 h-full">
          {renderLinks(currentLinks)}
        </div>
      </CSSTransition>
    </TransitionGroup>
  );
};

const NavigationDrawer = ({
  isOpen,
  setIsOpen,
  links,
  additionalHeaderLinks,
  languageList,
  block,
}: MobileMenuProps) => {
  const [activePath, setActivePath] = useState<number[]>([]);
  const [parentLinkName, setParentLinkName] = useState<string | null>(null);

  const handleGoBack = () => {
    const newPath = activePath.slice(0, -1);
    setActivePath(newPath);
    setParentLinkName(getParentLinkName(links, newPath));
  };

  // Only close the menu, reset state after closing
  const handleCloseMenu = () => {
    setIsOpen(false);
  };

  // Reset menu state after closing
  useEffect(() => {
    if (!isOpen) {
      setActivePath([]);
      setParentLinkName(null);
    }
  }, [isOpen]);

  const headerContent = (
    <div className="flex flex-col w-full navigation-drawer__header">
      <button
        className="inline-flex flex-row items-center justify-start pr-4 py-[18px] self-start "
        onClick={() => handleCloseMenu()}
        aria-label={translate('menu.close-menu')}
      >
        <div className="p-[0.813rem] rounded-full w-[43px] h-[43px] mr-5 flex items-center justify-center navigation-drawer__close-button">
          <i className="fal fa-times text-2xl text-white"></i>
        </div>
        <span className="text-[0.813rem] text-white uppercase navigation-drawer__close-button-text">
          {translate('navigation.closemenu')}
        </span>
      </button>
      {activePath.length > 0 && (
        <button
          className="flex navigation-drawer__go-back  py-4 gap-2 w-[calc(100%+44px)] !-mx-[22px] px-[22px] items-center justify-start"
          onClick={handleGoBack}
        >
          <i className="fal fa-arrow-left text-white "></i>
          {parentLinkName}
        </button>
      )}
    </div>
  );
  const footerContent = (
    <div className="flex justify-between items-center navigation-drawer__footer">
      {
        <div
          className="w-full"
          dangerouslySetInnerHTML={{
            __html: block,
          }}
        ></div>
      }
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={() => handleCloseMenu()}
      closeButtonAriaLabel={translate('menu.close-menu')}
      disableCloseOnOutsideClick={false}
      drawerPosition="left"
      className="navigation-drawer__dialog"
      dialogId="main-navigation-menu"
      footerContent={footerContent}
      headerContent={headerContent}
      headerClassName="!px-[22px]"
      hideCloseButton={true}
      footerClassName="!p-0 !border-t-0"
    >
      <nav className="h-full flex flex-col">
        <NavigationPanel
          activePath={activePath}
          setActivePath={setActivePath}
          links={links}
          additionalHeaderLinks={additionalHeaderLinks}
          languageList={languageList}
          setParentLinkName={setParentLinkName}
          parentLinkName={parentLinkName}
        />
      </nav>
    </Drawer>
  );
};

export default NavigationDrawer;
